import { CompanyLists_organization$key } from '#app/__generated__/relay/CompanyLists_organization.graphql.js';
import { CompanyListsPagination_organization$key } from '#app/__generated__/relay/CompanyListsPagination_organization.graphql.js';
import CompanyListsQueryNode, {
  CompanyListsQuery,
} from '#app/__generated__/relay/CompanyListsQuery.graphql.js';
import { AvatarGroup } from '#app/components/Avatars/AvatarGroup/AvatarGroup.js';
import { NewWorkOpportunitySkeleton } from '#app/components/SkeletonLoaders/NewWorkOpportunitySkeleton.js';
import { TextLink } from '#app/components/TextLink/TextLink.js';
import { useIsMobile } from '#app/hooks/useIsMobile.js';
import { usePaginatedInfiniteLoad } from '#app/hooks/usePaginatedInfiniteLoad.js';
import { formatName } from '#app/utilities/formatName.js';
import { OverviewUserListsSectionEmptyState } from '../EmptyStates/OverviewUserListsSectionEmptyState.js';
import { ExternalLinkAltIcon } from '@contra/icons/icons';
import {
  graphql,
  useFragment,
  useLazyLoadQuery,
  usePaginationFragment,
} from '@contra/relay';
import { getRoute } from '@contra/routes';
import { useCallback } from 'react';
import { css, styled } from 'styled-components';

const Column = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    width: 100%;
    border-radius: 20px;
    padding: 0 24px;
    border: 1px solid ${theme.colorsV2.gray20};

    & > * + * {
      border-top: 1px solid ${theme.colorsV2.gray20};
    }
  `}
`;

const TitleAvatarsColumns = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const LinkBox = styled.div`
  margin-top: auto;
  font-size: 14px;

  & > a > span {
    display: flex;
    align-items: center;
    gap: 4px;
  }
`;

const Name = styled.div`
  font-family: Inter;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: 0px;
  text-align: left;
`;

const Avatars = styled.div`
  display: flex;
  justify-self: flex-start;
  flex-direction: row;
  gap: 8px;
`;

const Card = styled.div`
  width: 100%;
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  gap: 32px;
  padding: 28px 0;
`;

type CompanyListsProps = {
  readonly organizationRef: CompanyLists_organization$key;
};

export const CompanyLists = ({ organizationRef }: CompanyListsProps) => {
  const organizationData = useFragment<CompanyLists_organization$key>(
    graphql`
      fragment CompanyLists_organization on Organization {
        id
        visitorIsMember
        ...OverviewUserListsSectionEmptyState_organization
      }
    `,
    organizationRef,
  );

  const query = useLazyLoadQuery<CompanyListsQuery>(
    CompanyListsQueryNode,
    {
      first: 10,
      id: organizationData.id,
    },
    { fetchPolicy: 'store-and-network' },
  );

  const { data, hasNext, isLoadingNext, loadNext } = usePaginationFragment<
    CompanyListsQuery,
    CompanyListsPagination_organization$key
  >(
    graphql`
      fragment CompanyListsPagination_organization on Organization
      @argumentDefinitions(
        first: { type: "Int", defaultValue: 5 }
        after: { type: "String" }
      )
      @refetchable(queryName: "CompanyListsQuery") {
        userLists(
          first: $first
          after: $after
          filter: { groupNid: NETWORK_LIST }
        ) @connection(key: "CompanyLists_userLists") {
          count
          pageInfo {
            hasNextPage
            hasPreviousPage
          }
          edges {
            node {
              id
              displayName
              slug
              userProfilesV2(
                first: 5
                filter: { hideIncompleteProfiles: true }
              ) {
                count
                edges {
                  node {
                    firstName
                    lastName
                    avatarImage {
                      uid
                    }
                  }
                }
              }
            }
          }
        }
      }
    `,
    query.node,
  );

  const loadMore = useCallback(() => {
    loadNext(10);
  }, [loadNext]);

  const loadMoreElementRef = usePaginatedInfiniteLoad<HTMLDivElement>(
    loadMore,
    hasNext,
    isLoadingNext,
    { rootMargin: '200px', threshold: 0 },
  );

  const isMobile = useIsMobile();

  if (data?.userLists.count === 0) {
    if (organizationData.visitorIsMember) {
      return (
        <Column>
          <OverviewUserListsSectionEmptyState
            organizationRef={organizationData}
          />
        </Column>
      );
    } else {
      return null;
    }
  }

  return (
    <>
      <Column>
        {data?.userLists.edges.map(({ node: list }) => (
          <Card key={list.displayName}>
            <TitleAvatarsColumns>
              <Name>{list.displayName}</Name>
              <Avatars>
                <AvatarGroup
                  data={list.userProfilesV2.edges.map(({ node }, index) => ({
                    avatarImage: node.avatarImage?.uid
                      ? {
                          uid: node.avatarImage?.uid,
                        }
                      : undefined,
                    fallbackName: formatName(
                      {
                        firstName: node.firstName,
                        lastName: node.lastName,
                      },
                      'fullName',
                    ),
                    key: node.avatarImage?.uid ?? `list-user-account-${index}`,
                  }))}
                  dataCount={list.userProfilesV2.count}
                  maxCount={isMobile ? 2 : 5}
                  size="sm"
                />
              </Avatars>
            </TitleAvatarsColumns>
            <LinkBox>
              <TextLink
                to={getRoute('/lists/@slug', {
                  args: {
                    slug: list.slug,
                  },
                })}
              >
                View list <ExternalLinkAltIcon size={16} />
              </TextLink>
            </LinkBox>
          </Card>
        ))}
      </Column>
      <NewWorkOpportunitySkeleton
        isLoading={isLoadingNext}
        ref={loadMoreElementRef}
      />
    </>
  );
};
